import { Component, Input, OnInit } from '@angular/core';
import { InjectConfig, ModelsExperienceConfigKey, SharedConfigKey } from '@ddt-web/config-layer';
import { ComponentData, DateFormatter, TabData, TableData } from '@ddt-web/data-service';
import { DateUtil } from '@ddt-web/generic-utils';
import { PerformanceUtil } from '@ddt-web/performance';
import { UntilDestroy } from '@ngneat/until-destroy';
import * as cloneDeep from 'lodash/cloneDeep';
import * as flatten from 'lodash/flatten';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import * as isNil from 'lodash/isNil';
import * as zip from 'lodash/zip';

@UntilDestroy()
@Component({
	selector: 'mb-performance-table',
	templateUrl: './performance-table.component.html',
	styleUrls: ['./performance-table.component.scss']
})
export class PerformanceTableComponent implements OnInit {
	@InjectConfig(SharedConfigKey.EnableCustomFee) private readonly isCustomFeeEnabled: boolean = true;
	@InjectConfig(ModelsExperienceConfigKey.ENABLE_PERFORMANCE_BENCHMARK) private readonly enablePerformanceBenchmarkData = false;
	@InjectConfig(ModelsExperienceConfigKey.PERFORMANCE_TABLE_DISABLE_COLUMNS_LIST) private readonly excludeColumns: string[] = ['id', 'modelName'];
	@InjectConfig(ModelsExperienceConfigKey.PERFORMANCE_TABLE_COLUMN_WIDTH) private readonly columnWidths: number[] = [19, 9, 9, 9, 9, 9, 9, 9, 9, 9];
	@InjectConfig(ModelsExperienceConfigKey.DATE_FORMAT) private readonly dateFormat: string = DateUtil.MODELS_US_DATE_FORMAT;

	@Input() public data: ComponentData;
	@Input() public familyId: string;
	@Input() public customAdvisoryFee: number;
	public asOfDate: string;
	public tableData: TableData;
	public validTabs: TabData[] = [];

	public readonly specificColumnAsHeaderY: string = 'modelName';

	public ngOnInit(): void {
		this.validTabs = [];
		this.preparePerformanceTableData();
	}

	private preparePerformanceTableData(): void {
		if (this.data.success) {
			const workingData = cloneDeep(this.data);
			const performanceTabData = workingData.getTabById('performance');
			const performanceCustomNetTabData = workingData.getTabById('performance.custom.net');
			const performanceBenchmarkTabData = workingData.getTabById('performance.benchmark');

			const performanceTableData = get(performanceTabData, 'tableData[0]', []);
			const performanceCustomNetTableData = get(performanceCustomNetTabData, 'tableData[0]', []);

			let combinedCells, combinedCustomNetCells;

			if (!isEmpty(performanceTableData)) {
				this.asOfDate = new DateFormatter(this.dateFormat).format(get(performanceTableData, 'rows[0].asOfDate.formattedValue'));
				const performanceCells = get(performanceTableData, 'cells', []);
				const performanceCustomNetCells = get(performanceCustomNetTableData, 'cells', []);
				if (this.enablePerformanceBenchmarkData) {
					const performanceBenchmarkTableData = get(performanceBenchmarkTabData, 'tableData[0]', []);
					if (!isEmpty(performanceBenchmarkTableData)) {
						const performanceBenchmarkCells = get(performanceBenchmarkTableData, 'cells', []);
						// combined table match the row by index, since benchmark row doesn't contain net/custom net performance, we need dummy cell to fill up the index
						const dummyBenchmarkCells = cloneDeep(performanceBenchmarkCells).map(cell => []);
						combinedCells = flatten(zip(performanceCells, performanceBenchmarkCells));
						combinedCustomNetCells = flatten(zip(performanceCustomNetCells, dummyBenchmarkCells));
					}
				} else {
					combinedCells = performanceCells;
					combinedCustomNetCells = performanceCustomNetCells;
				}
			}

			this.tableData = Object.assign(performanceTableData, { cells: combinedCells });
			Object.assign(performanceCustomNetTableData, { cells: combinedCustomNetCells });
			this.validTabs.push(performanceTabData);
			if (!isNil(this.customAdvisoryFee) && this.isCustomFeeEnabled) {
				PerformanceUtil.updateNetCustomPerformanceCells(performanceCustomNetTabData, this.customAdvisoryFee);
				this.validTabs.push(performanceCustomNetTabData);
			}
		}
	}
}
